import type { NoteDto } from '@keep/shared';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useNotes } from '../api/notes';
import { toast } from '../stores/toasts';

const STORAGE_KEY = 'keep.notifiedReminders';
const CHECK_INTERVAL_MS = 20_000;
/** Un rappel manqué de plus d'une heure (onglet fermé) n'est plus notifié. */
const MAX_LATENESS_MS = 60 * 60 * 1000;

function loadNotified(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function saveNotified(value: Record<string, string>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // stockage indisponible : au pire on notifiera deux fois
  }
}

export async function requestNotificationPermission() {
  if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
    await Notification.requestPermission().catch(() => undefined);
  }
}

/**
 * Déclenche les rappels échus tant que l'application est ouverte :
 * notification système si autorisée, sinon simple message dans l'app.
 */
export function useReminderNotifications() {
  const { data: notes } = useNotes({ view: 'reminders' });
  const navigate = useNavigate();

  useEffect(() => {
    if (!notes?.length) return;

    const check = () => {
      const now = Date.now();
      const notified = loadNotified();
      let changed = false;
      for (const note of notes) {
        if (!note.reminderAt) continue;
        const due = new Date(note.reminderAt).getTime();
        // On mémorise la date notifiée : si le rappel est reprogrammé, il sera notifié à nouveau.
        if (due > now || now - due > MAX_LATENESS_MS || notified[note.id] === note.reminderAt)
          continue;
        notified[note.id] = note.reminderAt;
        changed = true;
        notify(note, () => navigate(`?note=${note.id}`));
      }
      if (changed) saveNotified(notified);
    };

    check();
    const timer = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [notes, navigate]);
}

function notify(note: NoteDto, open: () => void) {
  const title = note.title || 'Rappel';
  const body = note.content || note.items.map((i) => i.text).join(', ');
  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    const notification = new Notification(title, {
      body: body.slice(0, 200),
      tag: note.id,
      icon: '/favicon.svg',
    });
    notification.onclick = () => {
      window.focus();
      open();
    };
  }
  toast(`Rappel : ${title}`, { action: { label: 'Ouvrir', onClick: open }, duration: 10_000 });
}
